import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useContent } from '@backstage/core-components';
import {
  SearchContextProvider,
  useSearch,
} from '@backstage/plugin-search-react';
import { Box } from '@backstage/ui';
import useClickAway from 'react-use/esm/useClickAway';

import { SearchBar } from './SearchBar';
import { SearchDropDown } from '../search-dropdown/SearchDropdown';
import styles from './GlobalSearch.module.css';

/**
 * The delay before the content takes focus after a result opens: the MUI
 * leaving-screen transition the search bar waited for before.
 */
const FOCUS_CONTENT_DELAY_MS = 195;

const GlobalSearchBase = () => {
  const { term, setTerm } = useSearch();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { focusContent } = useContent();
  const location = useLocation();
  const isSearchPage = location.pathname === '/search';
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!isSearchPage) {
      setOpen(Boolean(term));
    }
  }, [isSearchPage, term]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSearchPage) {
        setTerm('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchPage, setTerm]);

  useEffect(() => {
    const queryParam = new URLSearchParams(location.search).get('query');
    setTerm(queryParam ?? '');
  }, [location.search, setTerm]);

  const handleSearchResultClick = useCallback(() => {
    setTimeout(focusContent, FOCUS_CONTENT_DELAY_MS);
  }, [focusContent]);

  const handleSubmit = useCallback(
    (query: string) => {
      if (!query) {
        return;
      }
      navigate(`/search?query=${query}`);
      handleSearchResultClick();
      setOpen(false);
    },
    [navigate, handleSearchResultClick],
  );

  const handleSearchChange = (value: string) => {
    setTerm(value);

    if (isSearchPage) {
      const updatedParams = new URLSearchParams(window.location.search);

      if (value) {
        updatedParams.set('query', value);
      } else {
        updatedParams.delete('query');
      }

      setSearchParams(updatedParams);
    }
  };

  useClickAway(containerRef, () => {
    if (!isSearchPage) {
      setTerm('');
    }
  });

  return (
    <Box className={styles.container} ref={containerRef}>
      <SearchBar onSubmit={handleSubmit} onChange={handleSearchChange} />
      {!isSearchPage && open && (
        <SearchDropDown
          onViewAll={() => handleSubmit(term)}
          onResultClick={() => {
            setTerm('');
          }}
        />
      )}
    </Box>
  );
};

export const GlobalSearch = () => {
  return (
    <SearchContextProvider inheritParentContextIfAvailable>
      <GlobalSearchBase />
    </SearchContextProvider>
  );
};
